import { createSlice } from '@reduxjs/toolkit';
import { QuestionnaireState } from 'src/models';
import { asyncIsFulfilled, asyncIsPending, asyncIsRejected } from '../asyncConfig';
import {
  getAgentBrand,
  getClientKYCFiles,
  getClientQuestionnaires, getCountries, getKYCSectionMatch, getKYCSectionTitles, getKYCServicesTypes, getMyProfile, reAuthenticate, securityCodeSetup, submitQuestionnaireAns,
  verifyToken
} from './questionnaireService';

const initialState: QuestionnaireState = {
  isAuthenticated: null,
  message: '',
  status: null,
  questionnaires: [],
  countries: [],
  kyc_section_titles: [],
  docs_uploaded: [],
  docs_to_upload: {},
  tabIndex: 0,
  activeTitleId: null,
  brand: {
    brand_color: '',
    name: '',
    logo: ''
  },
  client: null,
  matches: [],
  visa_types: [],
}

export const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    clearApplicationState: (state) => {
      state.status = null;
      state.isAuthenticated = null
      state.message = ''
    },
    unAuthenticate: (state) => {
      state.status = null;
      state.isAuthenticated = false
      state.message = ''
    },
    getClientProfile: (state, action) => {
      state.isAuthenticated = null
      const client = JSON.parse(localStorage.getItem('daps-client-spa') ?? '{}')
      if (Object.keys(client).length > 1 && client.uuid === action.payload) {
        state.client = client
      } else {
        state.isAuthenticated = false;
      }
      state.status = null;
      state.message = ''
    },
    setTabIndex: (state, action) => {
      state.status = null;
      state.message = '';
      state.tabIndex = action.payload;
    },
    setActiveTitleId: (state, action) => {
      state.status = null;
      state.message = '';
      state.activeTitleId = action.payload;
    },
    resetDocsToUpload: (state, action) => {
      state.status = null;
      state.message = '';
      state.docs_to_upload = action.payload;
    },
    resetUploadedDocs: (state, action) => {
      state.status = null;
      state.message = '';
      state.docs_uploaded = action.payload;
    },
    resetQuestionnairesWithNewAnswers: (state, action) => {
      state.status = null;
      state.message = '';
      state.questionnaires = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(submitQuestionnaireAns.pending, (state, _) => {
      state.status = 'pending';
      state.message = '';
      state.action = 'submit';
    })
    builder.addCase(submitQuestionnaireAns.rejected, asyncIsRejected)
    builder.addCase(submitQuestionnaireAns.fulfilled, asyncIsFulfilled)
    builder.addCase(getClientQuestionnaires.pending, asyncIsPending)
    builder.addCase(getClientQuestionnaires.rejected, asyncIsRejected)
    builder.addCase(getClientQuestionnaires.fulfilled, (state, action) => {
      state.status = null;
      state.isAuthenticated = true;
      state.questionnaires = action.payload;
    })
    builder.addCase(getClientKYCFiles.pending, asyncIsPending)
    builder.addCase(getClientKYCFiles.rejected, asyncIsRejected)
    builder.addCase(getClientKYCFiles.fulfilled, (state, action) => {
      state.status = null;
      state.isAuthenticated = true;
      state.docs_uploaded = action.payload;
    })
    builder.addCase(getKYCServicesTypes.pending, asyncIsPending)
    builder.addCase(getKYCServicesTypes.rejected, asyncIsRejected)
    builder.addCase(getKYCServicesTypes.fulfilled, (state, action) => {
      state.status = null;
      state.visa_types = action.payload;
    })
    builder.addCase(getCountries.pending, asyncIsPending)
    builder.addCase(getCountries.rejected, asyncIsRejected)
    builder.addCase(getCountries.fulfilled, (state, action) => {
      state.status = null;
      state.countries = action.payload.countries;
    })
    builder.addCase(getAgentBrand.pending, asyncIsPending)
    builder.addCase(getAgentBrand.rejected, asyncIsRejected)
    builder.addCase(getAgentBrand.fulfilled, (state, action) => {
      state.status = null;
      state.brand = action.payload;
    })
    builder.addCase(getKYCSectionMatch.pending, asyncIsPending)
    builder.addCase(getKYCSectionMatch.rejected, asyncIsRejected)
    builder.addCase(getKYCSectionMatch.fulfilled, (state, action) => {
      state.status = null;
      state.matches = action.payload;
    })
    builder.addCase(getKYCSectionTitles.pending, asyncIsPending)
    builder.addCase(getKYCSectionTitles.rejected, asyncIsRejected)
    builder.addCase(getKYCSectionTitles.fulfilled, (state, action) => {
      state.status = null;
      state.kyc_section_titles = action.payload;
    })
    builder.addCase(getMyProfile.pending, asyncIsPending)
    builder.addCase(getMyProfile.rejected, asyncIsRejected)
    builder.addCase(getMyProfile.fulfilled, (state, action) => {
      if (action.payload.client) {
        localStorage.setItem('daps-client-spa', JSON.stringify(action.payload.client))
      }
      state.status = null;
      state.isAuthenticated = true;
      state.client = action.payload.client;
    })
    builder.addCase(verifyToken.pending, asyncIsPending)
    builder.addCase(verifyToken.rejected, asyncIsRejected)
    builder.addCase(verifyToken.fulfilled, (state, action) => {
      if (action.payload.client) {
        localStorage.setItem('daps-client-spa', JSON.stringify(action.payload.client))
      }
      state.status = 'fulfilled';
      state.message = action.payload.message;
      state.isAuthenticated = false;
      state.client = action.payload.client;
    })
    builder.addCase(securityCodeSetup.pending, asyncIsPending)
    builder.addCase(securityCodeSetup.rejected, asyncIsRejected)
    builder.addCase(securityCodeSetup.fulfilled, (state, action) => {
      if (action.payload.client) {
        localStorage.setItem('daps-client-spa', JSON.stringify(action.payload.client))
      }
      state.status = 'fulfilled';
      state.isAuthenticated = true;
      state.message = action.payload.message;
      state.client = action.payload.client;
    })
    builder.addCase(reAuthenticate.pending, asyncIsPending)
    builder.addCase(reAuthenticate.rejected, asyncIsRejected)
    builder.addCase(reAuthenticate.fulfilled, (state, action) => {
      if (action.payload.client) {
        localStorage.setItem('daps-client-spa', JSON.stringify(action.payload.client))
      }
      state.status = 'fulfilled';
      state.isAuthenticated = true;
      state.message = action.payload.message;
      state.client = action.payload.client;
    })
  }
});

export const {
  getClientProfile,
  clearApplicationState,
  setActiveTitleId,
  setTabIndex,
  resetQuestionnairesWithNewAnswers,
  resetDocsToUpload,
  resetUploadedDocs,
  unAuthenticate
} = questionnaireSlice.actions;
export default questionnaireSlice.reducer;